import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} SellingPlan
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef {Object} SellingPlanGroup
 * @property {string} id
 * @property {string} name
 * @property {SellingPlan[]} sellingPlans
 */

/**
 * @typedef {Object} AllocationData
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {number} perDeliveryPrice
 */

/**
 * @typedef {Object} SellingPlanData
 * @property {number} variantId
 * @property {SellingPlanGroup[]} groups
 * @property {Record<string, AllocationData>} allocations
 */

/**
 * @typedef {Object} SubscriptionWidgetRefs
 * @property {HTMLScriptElement} sellingPlanData - JSON data script element
 * @property {HTMLInputElement} sellingPlanInput - Hidden selling plan form input
 * @property {HTMLSelectElement} frequencySelect - Frequency dropdown
 * @property {HTMLElement} subscriptionOption - Subscription option card
 * @property {HTMLElement} [onetimeOption] - One-time option card (optional, may not exist for subscription-only products)
 * @property {HTMLInputElement} subscriptionRadio - Subscription radio input
 * @property {HTMLInputElement} [onetimeRadio] - One-time radio input
 * @property {HTMLElement} subscriptionPrice - Subscription price display
 * @property {HTMLElement} [onetimePrice] - One-time price display
 * @property {HTMLElement} frequencyBlock - Frequency selector container
 * @property {HTMLElement} [unitPrice] - Unit price placeholder
 * @property {HTMLElement} [discountLabel] - Automatic discount percentage label
 * @property {HTMLElement} [discountLabelSr] - Screen-reader-only discount label
 */

/**
 * Subscription purchase options widget.
 *
 * Allows customers to toggle between subscription and one-time purchase,
 * with frequency selection and selling-plan-aware pricing.
 *
 * @extends {Component<SubscriptionWidgetRefs>}
 */
class SubscriptionWidgetComponent extends Component {
  requiredRefs = ['sellingPlanData', 'sellingPlanInput', 'frequencySelect'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {SellingPlanData | null} */
  #data = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#parseData();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Listen for sibling AJAX navigation (morph without element recreation)
    window.addEventListener('sibling:navigated', this.#onSiblingNavigated, { signal });

    // Defer initial emit so the price block's listeners are ready
    queueMicrotask(() => {
      if (this.refs.subscriptionRadio?.checked) {
        this.#emitPlanChange('subscription');
      }
    });
  }

  /**
   * Handles sibling AJAX navigation (morph without element recreation).
   * Re-parses data and re-emits plan state so the price block stays in sync.
   */
  #onSiblingNavigated = () => {
    this.#parseData();
    queueMicrotask(() => {
      const isSubscription = this.refs.subscriptionRadio?.checked;
      this.#emitPlanChange(isSubscription ? 'subscription' : 'onetime');
    });
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  // ---------------------------------------------------------------------------
  // Event handlers (wired via on:change in Liquid)
  // ---------------------------------------------------------------------------

  /**
   * Handles toggling between subscription and one-time purchase options.
   *
   * @param {Event} event
   */
  handleOptionChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const isSubscription = target.value === 'subscription';

    // Toggle active class on option cards (CSS handles show/hide of advantages, labels, subtitle)
    this.refs.subscriptionOption?.classList.toggle('radio-card--active', isSubscription);
    this.refs.onetimeOption?.classList.toggle('radio-card--active', !isSubscription);

    // Toggle hidden input name so the product form includes/excludes selling_plan
    const input = this.refs.sellingPlanInput;

    if (isSubscription) {
      input.name = 'selling_plan';
      input.value = this.refs.frequencySelect.value;
    } else {
      input.name = '';
      input.value = '';
    }

    this.#emitPlanChange(isSubscription ? 'subscription' : 'onetime');
  }

  /**
   * Handles frequency (selling plan) dropdown change.
   *
   * Always refreshes the in-card price preview and discount label so the
   * customer can browse subscription frequencies without committing.
   * Only propagates the plan change to external listeners (main price,
   * shipping-info text, etc.) when the subscription radio is actually
   * selected — otherwise the user is just previewing.
   *
   * @param {Event} event
   */
  handleFrequencyChange(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    const sellingPlanId = target.value;

    // Update the hidden input value so it's ready if subscription is enabled later.
    // Form submission is gated by `input.name`, which is set in handleOptionChange.
    this.refs.sellingPlanInput.value = sellingPlanId;

    // In-card preview updates regardless of radio state.
    this.#updatePriceDisplay(sellingPlanId);
    this.#updateDiscountLabel(sellingPlanId);

    // Only emit when subscription is actually the active purchase option.
    if (this.refs.subscriptionRadio?.checked) {
      this.#emitPlanChange('subscription');
    }
  }

  // ---------------------------------------------------------------------------
  // Public API (for external components like bundle builder)
  // ---------------------------------------------------------------------------

  /**
   * Returns the currently selected selling plan ID, or null if one-time is selected.
   * @returns {string | null}
   */
  get selectedSellingPlanId() {
    const isSubscription = this.refs.subscriptionRadio?.checked;
    return isSubscription ? this.refs.sellingPlanInput.value : null;
  }

  /**
   * Updates the widget's selling plan data for a new variant.
   * Used by external components (e.g., bundle builder) when the variant changes.
   * @param {SellingPlanData & { variantPrice?: number }} data
   */
  updateVariantData(data) {
    this.#data = data;
    const selectedPlanId = this.refs.frequencySelect.value;
    this.#updatePriceDisplay(selectedPlanId);
    this.#updateDiscountLabel(selectedPlanId);

    // Update one-time price if element exists and variant price is provided
    if (this.refs.onetimePrice && data.variantPrice != null) {
      this.refs.onetimePrice.textContent = this.#formatMoney(data.variantPrice);
    }
  }

  // ---------------------------------------------------------------------------
  // Variant integration
  // ---------------------------------------------------------------------------

  /**
   * Reacts to variant changes from the variant picker.
   * Updates prices and selling plan allocations for the new variant.
   *
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;

    if (!variant || !this.#data) return;
    if (event.detail.data.productId !== this.dataset.productId) return;

    const html = event.detail.data.html;

    if (!html) return;

    // Find the updated subscription widget in the new HTML
    const newWidget = html.querySelector('subscription-widget-component');

    if (!newWidget) return;

    // Re-parse selling plan data from the new HTML
    const newDataScript = newWidget.querySelector('[ref="sellingPlanData"]');

    if (newDataScript) {
      try {
        this.#data = JSON.parse(newDataScript.textContent || '');
      } catch (error) {
        if (typeof window !== 'undefined' && window.Shopify?.designMode) {
          console.warn('[SubscriptionWidget] Failed to parse updated selling plan data:', error.message);
        }
        return;
      }
    }

    // Update prices
    const selectedPlanId = this.refs.frequencySelect.value;
    this.#updatePriceDisplay(selectedPlanId);

    // Update discount label
    this.#updateDiscountLabel(selectedPlanId);

    // Update one-time price if available
    if (this.refs.onetimePrice) {
      const newOnetimePrice = newWidget.querySelector('[ref="onetimePrice"]');

      if (newOnetimePrice) {
        // Clone the element content to avoid XSS while preserving structure
        while (this.refs.onetimePrice.firstChild) {
          this.refs.onetimePrice.removeChild(this.refs.onetimePrice.firstChild);
        }
        for (const child of newOnetimePrice.childNodes) {
          this.refs.onetimePrice.appendChild(child.cloneNode(true));
        }
      }
    }

    // Defer re-emit so it fires after product-price.updatePrice has swapped the DOM
    const isSubscription = this.refs.subscriptionRadio?.checked;
    queueMicrotask(() => {
      this.#emitPlanChange(isSubscription ? 'subscription' : 'onetime');
    });
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Parses the JSON selling plan data embedded in the component.
   */
  #parseData() {
    try {
      this.#data = JSON.parse(this.refs.sellingPlanData.textContent || '');
    } catch (error) {
      this.#data = null;
      if (typeof window !== 'undefined' && window.Shopify?.designMode) {
        console.warn('[SubscriptionWidget] Failed to parse selling plan data:', error.message);
      }
    }
  }

  /**
   * Updates the subscription price display from the allocation data
   * for a given selling plan ID.
   *
   * @param {string} sellingPlanId
   */
  #updatePriceDisplay(sellingPlanId) {
    if (!this.#data) return;

    const allocation = this.#data.allocations[sellingPlanId];
    if (!allocation) return;

    const priceEl = this.refs.subscriptionPrice;
    if (!priceEl) return;

    // Format price using Shopify's money format
    const formattedPrice = this.#formatMoney(allocation.perDeliveryPrice);
    let html = '';

    if (allocation.compareAtPrice > allocation.price) {
      const formattedCompare = this.#formatMoney(allocation.compareAtPrice);
      html += `<s class="subscription-widget__compare-price">${formattedCompare}</s> `;
    }

    html += formattedPrice;
    priceEl.innerHTML = html;

    // Announce price change to screen readers
    this.#announcePrice(formattedPrice);
  }

  /**
   * Updates the discount percentage label from the allocation data
   * for a given selling plan ID.
   *
   * @param {string} sellingPlanId
   */
  #updateDiscountLabel(sellingPlanId) {
    if (!this.#data) return;

    const discountLabel = this.refs.discountLabel;
    const discountLabelSr = this.refs.discountLabelSr;
    if (!discountLabel) return;

    const allocation = this.#data.allocations[sellingPlanId];
    if (!allocation) return;

    // Calculate discount percentage (2 decimals, floor to avoid overstating discount)
    if (allocation.compareAtPrice > allocation.price) {
      const discountAmount = allocation.compareAtPrice - allocation.price;
      const discountPercentageRaw = (discountAmount / allocation.compareAtPrice) * 100;
      // Floor to 2 decimals without rounding up
      const discountPercentage = (Math.floor(discountPercentageRaw * 100) / 100).toFixed(2);

      // Use translation template from data attribute (set in Liquid)
      const template = this.dataset.saveLabelTemplate || 'SAVE __PERCENTAGE__%';
      const newText = template.replace('__PERCENTAGE__', discountPercentage);

      discountLabel.textContent = newText;
      discountLabel.classList.remove('subscription-widget__label--hidden');

      // Update screen-reader label too
      if (discountLabelSr) {
        discountLabelSr.textContent = newText;
      }
    } else {
      // Hide label if no discount
      discountLabel.classList.add('subscription-widget__label--hidden');
      if (discountLabelSr) {
        discountLabelSr.textContent = '';
      }
    }
  }

  /**
   * Formats a price in cents to the shop's money format.
   * Uses Shopify's global money format if available, otherwise falls back to basic formatting.
   *
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  #formatMoney(cents) {
    if (typeof Theme !== 'undefined' && Theme.moneyFormat) {
      const amount = cents / 100;
      const format = Theme.moneyFormat;

      return format.replace(/\{\{\s*(amount\w*)\s*\}\}/, (_, placeholder) => {
        return this.#formatWithDelimiters(amount, placeholder);
      });
    }

    return `${(cents / 100).toFixed(2)} €`;
  }

  /**
   * Formats a number with the correct delimiters for the given Shopify money format placeholder.
   *
   * @param {number} amount
   * @param {string} placeholder
   * @returns {string}
   */
  #formatWithDelimiters(amount, placeholder) {
    /** @type {[number, string, string]} */
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', '.'],
      amount_with_space_separator: [2, ' ', ','],
    };

    const [precision, thousands, decimal] = formats[placeholder] || [2, ',', '.'];
    const fixed = amount.toFixed(precision);
    const parts = fixed.split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return precision > 0 ? intPart + decimal + parts[1] : intPart;
  }

  /**
   * Announces a price change to screen readers via the live region.
   *
   * @param {string} formattedPrice
   */
  #announcePrice(formattedPrice) {
    const el = this.refs.priceAnnouncement;
    if (!el) return;

    // Clear first so repeated identical values still trigger an announcement
    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = formattedPrice;
    });
  }

  /**
   * Extracts the subscription price from the widget's DOM and reformats it
   * to match the structure used by snippets/price.liquid.
   * Reads directly from refs.subscriptionPrice which is always up-to-date
   * (server-rendered on load, updated by JS on frequency/variant changes).
   *
   * @returns {string | null}
   */
  #extractPriceHTML() {
    const el = this.refs.subscriptionPrice;
    if (!el) return null;

    const compareEl = el.querySelector('s');

    if (compareEl) {
      const compareText = compareEl.textContent.trim();
      const saleText = el.textContent.replace(compareText, '').trim();
      // Match snippets/price.liquid structure (show_sale_price_first: true)
      return (
        `<span role="group"><span class="price">${saleText}</span></span>` +
        `<span role="group"><span class="compare-at-price">${compareText}</span></span>`
      );
    }

    return `<span class="price">${el.textContent.trim()}</span>`;
  }

  /**
   * Dispatches a custom event with the current plan selection details.
   * Includes pre-formatted price HTML read from the widget's own DOM,
   * so the price block can update without duplicating formatting logic.
   *
   * @param {'subscription' | 'onetime'} type
   */
  #emitPlanChange(type) {
    const priceHTML = type === 'subscription' ? this.#extractPriceHTML() : null;

    this.dispatchEvent(
      new CustomEvent('subscription-widget:plan-change', {
        bubbles: true,
        detail: {
          type,
          sellingPlanId: type === 'subscription' ? this.refs.sellingPlanInput.value : null,
          variantId: this.#data?.variantId ?? null,
          priceHTML,
        },
      })
    );
  }
}

if (!customElements.get('subscription-widget-component')) {
  customElements.define('subscription-widget-component', SubscriptionWidgetComponent);
}
